/* Cutxi atelier estimate — 10 Sep 2026. Independent, reversible; never changes fees or checkout.
   Future event-date validation belongs after resolve() / dates(), not in the transport estimate. */
(function (root) {
  'use strict';
  const norm = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  function parseDays(text) {
    const value = norm(text);
    // Read the number immediately before "dia(s)"; never read the price or an event date.
    const m = value.match(/(?:^|[^\d.,])(\d{1,3})(?:\s*(?:a|ate|e|[-–—])\s*(\d{1,3}))?\s*dias?\b/);
    if (!m) return null;
    const min = Number(m[1]), max = Number(m[2] || m[1]);
    return min <= max && max <= 120 ? [min, max] : null;
  }
  const validRange = r => Array.isArray(r) && r.length === 2 && r.every(n => Number.isInteger(n) && n >= 0 && n <= 120) && r[0] <= r[1];
  const combine = ranges => ranges.length && ranges.every(validRange) ? [Math.max(...ranges.map(r => r[0])), Math.max(...ranges.map(r => r[1]))] : null;
  // Workshops have no physical dispatch estimate, including in mixed carts.
  const excluded = p => Boolean(p.giftCard || (!(p.tags || []).some(t => /^prazo(3|5|8|15|20)$/.test(norm(t))) && (/workshop/.test(norm(p.template)) || (p.tags || []).some(t => /^workshops?$/.test(norm(t))))));
  function baseDays(p, config) {
    if (excluded(p)) return null;
    const days = p.effectiveDays;
    return Number.isInteger(days) && days >= 0 ? [days, days] : null;
  }

  const noUrgency = text => /^(?:nao|nenhum|nenhuma|sem)\b/.test(norm(text)) || /sem (?:prioridade|urgencia)/.test(norm(text));
  const urgencyKey = text => /urgencia|urgency|prioridade|prioritaria|acelerar.*producao|taxa.*eventos/.test(norm(text));
  function urgencyDays(properties, options) {
    const props = Array.isArray(properties) ? Object.fromEntries(properties) : properties || {}, candidates = [];
    let unknown = false;
    for (const [key, value] of Object.entries(props)) {
      if (key === '_urgency_level' || key === '_urgency_group' || !urgencyKey(key)) continue;
      if (!value || noUrgency(value)) continue;
      const texts = key === '_cutxi_atelier_urgency' ? String(value).split(' | ') : [value];
      for (const text of texts) {
        const range = parseDays(text);
        if (range) candidates.push(range); else unknown = true;
      }
    }
    for (const option of options || []) {
      if (!urgencyKey(option.name) || !option.value || noUrgency(option.value)) continue;
      const range = parseDays(option.value);
      if (range) candidates.push(range); else unknown = true;
    }
    // Older carts may hold only an internal code, with no actual selected lead time.
    if (props._urgency_level && !props._cutxi_atelier_urgency && !candidates.length) unknown = true;
    return { range: unknown ? null : combine(candidates), selected: unknown || candidates.length > 0 };
  }
  const blocksOneDay = p => (p.tags || []).some(t => norm(t) === 'sem-urgencia-1-dia');
  function invalidOneDay(p, properties, options) {
    if (!blocksOneDay(p)) return false;
    const props = Array.isArray(properties) ? Object.fromEntries(properties) : properties || {};
    if (norm(props._urgency_level) === 'urgente') return true;
    return Object.entries(props).some(([key, value]) => urgencyKey(key) && !noUrgency(value) && String(value).split(' | ').some(text => parseDays(text)?.[0] === 1))
      || (options || []).some(o => urgencyKey(o.name) && parseDays(o.value)?.[0] === 1);
  }
  function resolve(p, properties, options, config) {
    if (excluded(p) || p.effectiveDays < 0) return null;
    if (invalidOneDay(p, properties, options)) return baseDays(p, config);
    const urgent = urgencyDays(properties, options);
    const base = baseDays(p, config);
    return urgent.selected ? (urgent.range && base ? urgent.range.map(n => Math.min(n, base[1])) : urgent.range) : base;
  }
  function easter(year) {
    // Gregorian computus: movable holidays are calculated, not maintained year by year.
    const a=year%19,b=Math.floor(year/100),c=year%100,d=Math.floor(b/4),e=b%4,f=Math.floor((b+8)/25),g=Math.floor((b-f+1)/3),h=(19*a+b-d-g+15)%30,i=Math.floor(c/4),k=c%4,l=(32+2*e+2*i-h-k)%7,m=Math.floor((a+11*h+22*l)/451),n=h+l-7*m+114;
    return new Date(Date.UTC(year,Math.floor(n/31)-1,n%31+1,12));
  }
  const iso = date => date.toISOString().slice(0,10);
  function isWorking(date, config) {
    if ([0,6].includes(date.getUTCDay())) return false;
    if (String(config.closures || '').split(/\s+/).includes(iso(date))) return false;
    if (!config.holidays) return true;
    if (['01-01','04-25','05-01','06-10','08-15','10-05','11-01','12-01','12-08','12-25'].includes(iso(date).slice(5))) return false;
    const sunday = easter(date.getUTCFullYear());
    return ![-2,0,60].some(offset => { const d = new Date(sunday); d.setUTCDate(d.getUTCDate()+offset); return iso(d) === iso(date); });
  }
  function lisbonToday(now = new Date()) {
    const parts = new Intl.DateTimeFormat('en-GB',{timeZone:'Europe/Lisbon',year:'numeric',month:'2-digit',day:'2-digit'}).formatToParts(now);
    const get = key => Number(parts.find(p => p.type === key).value);
    return new Date(Date.UTC(get('year'),get('month')-1,get('day'),12));
  }
  function addDays(base, n, config) {
    const d = new Date(base);
    for (let added=0;added<n;) { d.setUTCDate(d.getUTCDate()+1); if(isWorking(d,config)) added++; }
    while(!isWorking(d,config)) d.setUTCDate(d.getUTCDate()+1);
    return d;
  }
  function intakeDate(config, now = new Date()) {
    const today = lisbonToday(now);
    const hour = Number(new Intl.DateTimeFormat('en-GB', {timeZone:'Europe/Lisbon',hour:'2-digit',hourCycle:'h23'}).format(now));
    if (hour >= 16 || !isWorking(today, config)) {
      today.setUTCDate(today.getUTCDate()+1);
      while (!isWorking(today, config)) today.setUTCDate(today.getUTCDate()+1);
    }
    return today;
  }
  const dates = (range, config, now) => range.map(n => addDays(intakeDate(config, now), n, config));
  function formatRange(range, config) {
    const [a,b] = range;
    const long = d => new Intl.DateTimeFormat(config.locale === 'en' ? 'en-GB' : 'pt-PT',{timeZone:'UTC',day:'numeric',month:'long',...(a.getUTCFullYear() !== b.getUTCFullYear() ? {year:'numeric'} : {})}).format(d);
    if (iso(a)===iso(b)) return config.text.on + ' ' + long(a);
    const first = a.getUTCMonth()===b.getUTCMonth() && a.getUTCFullYear()===b.getUTCFullYear() ? a.getUTCDate() : long(a);
    return config.text.between + ' ' + first + ' ' + (config.text.dateJoin || 'e') + ' ' + long(b);
  }
  const feeIds = ['57170989023576','57170989056344','57170989089112','57170989154648','57170989121880','57170989187416'];
  function cartRange(items, config) {
    const real = items.filter(i => !feeIds.includes(String(i.variantId)) && (Array.isArray(i.properties) ? Object.fromEntries(i.properties) : i.properties)?._cutxi_fee_type !== 'production_priority' && !excluded(i.product));
    return { count:real.length, range:combine(real.map(i => resolve(i.product,i.properties,i.options,config))) };
  }
  const core = {excluded,parseDays,baseDays,urgencyDays,resolve,easter,isWorking,lisbonToday,intakeDate,addDays,dates,formatRange,cartRange,combine};
  if(typeof module !== 'undefined' && module.exports) module.exports=core;
  if(!root.document) return;
  let config, timer, inflight=false, pending=false;
  function readConfig() { const el=document.getElementById('cutxi-atelier-settings'); try {config=JSON.parse(el.textContent);} catch {config=null;} }
  function renderBox(box, range, mode) {
    box.hidden=!config?.enabled;
    if(!config?.enabled) return;
    const text=range ? config.text[mode].replace('{date}',formatRange(dates(range,config),config)) : config.text[mode==='cart'?'cartUnknown':'unknown'];
    const output=box.querySelector('[data-atelier-result]');
    if(output.textContent!==text) output.textContent=text;
  }
  function productUpdate(box) {
    if(!config) return;
    const form=box.closest('form'); if(!form) return;
    let product; try {product=JSON.parse(box.querySelector('[data-atelier-product]').textContent);} catch{return;}
    if(excluded(product)) {
      box.hidden=true;
      const field=box.querySelector('[data-atelier-urgency]');
      if(field) {field.value='';field.disabled=true;}
      return;
    }
    const properties={}, options=[];
    // Native urgency is an internal code. Its label is the existing source of truth.
    const selected=form.querySelector('input[name="properties[_urgency_level]"]:checked');
    let selectedText=selected?.value ? selected.closest('label')?.querySelector('[data-atelier-source]')?.dataset.atelierSource || selected.closest('label')?.textContent.trim() || '' : '';
    for(const input of Array.from(form.elements)) {
      if(input.disabled || input.type==='hidden' || ['file','submit','button'].includes(input.type)) continue;
      if(['radio','checkbox'].includes(input.type) && !input.checked) continue;
      if(input.name==='properties[_urgency_level]') continue;
      const label=Array.from(input.labels || []).map(l=>l.textContent).join(' ');
      const optionContainer=input.closest('.cl-po--option');
      const title=optionContainer?.querySelector('label,.cl-po--label')?.textContent || '';
      if(!urgencyKey(input.name+' '+label+' '+title)) continue;
      const text=input.tagName==='SELECT' ? input.options[input.selectedIndex]?.textContent || input.value : (parseDays(input.value) || noUrgency(input.value) ? input.value : parseDays(label) ? label : input.value);
      if(text) properties[input.name || label || title]=text;
    }
    const appUrgency=urgencyDays(properties,options);
    // If the app supplies a deadline, preserve it alongside native text; max range is conservative.
    if(selectedText) properties._cutxi_atelier_urgency=selectedText;
    const selectedRanges=Object.values(properties).filter(v=>v&&!noUrgency(v));
    const hidden=box.querySelector('[data-atelier-urgency]');
    if(hidden) {hidden.value=config.enabled ? selectedRanges.join(' | ') : '';hidden.disabled=!config.enabled;}
    // Multiple active urgency controls must all parse; never silently drop an unknown choice.
    let urgent=urgencyDays(properties,options);
    if(appUrgency.selected && !appUrgency.range) urgent={selected:true,range:null};
    const base=baseDays(product,config);
    const defaultLabel=form.querySelector('input[name="properties[_urgency_level]"][value=""]')?.closest('label')?.querySelector('span');
    if(defaultLabel && !defaultLabel.hasAttribute('data-atelier-original-label'))defaultLabel.setAttribute('data-atelier-original-label',defaultLabel.textContent);
    if(defaultLabel && (!config.enabled || !base))defaultLabel.textContent=defaultLabel.getAttribute('data-atelier-original-label');
    if(defaultLabel && base && config.enabled) {
      const label=(base[0]===base[1] ? config.text.normalSingle : config.text.normal).replace('{min}',base[0]).replace('{max}',base[1]);
      if(defaultLabel.textContent!==label)defaultLabel.textContent=label;
    }
    const range=product.effectiveDays < 0?null:invalidOneDay(product,properties,options)?base:urgent.selected?(urgent.range && base ? urgent.range.map(n => Math.min(n,base[1])) : urgent.range):base;
    // Freeze the selected lead time and calendar on the order for Order Printer.
    // Dates are calculated from the order date; later theme/product edits cannot change this range.
    const snapshot = {
      _cutxi_atelier_days: range ? range.join('|') : '',
      _cutxi_atelier_calendar: config.holidays ? 'pt-v1' : 'weekdays-v1',
      _cutxi_atelier_closures: String(config.closures || '').split(/\s+/).filter(v=>/^\d{4}-\d{2}-\d{2}$/.test(v)).join(',')
    };
    for(const [key,value] of Object.entries(snapshot)) {
      let field=box.querySelector('input[name="properties['+key+']"]');
      if(!field) {field=document.createElement('input');field.type='hidden';field.name='properties['+key+']';box.appendChild(field);}
      field.disabled=!config.enabled || !range;
      field.value=field.disabled ? '' : value;
    }

    // Preserve old app markup but suppress standalone static estimates when the new estimate is available.
    form.querySelectorAll('.cl-po--option').forEach(el=>{
      const standalone=/^~?\s*\d+(?:\s*(?:a|e|[-–—])\s*\d+)?\s*dias?\s+uteis\s+estimados?\s+para\s+producao$/.test(norm(el.textContent));
      if(standalone && !el.querySelector('input,select,textarea,button')) {el.setAttribute('data-cutxi-legacy-estimate','');el.hidden=Boolean(config.enabled && range);}
    });
    renderBox(box,range,'product');
    if(product.effectiveDays < 0 && config.enabled)box.querySelector('[data-atelier-result]').textContent=config.text.preorder;
  }
  function productsUpdate() { document.querySelectorAll('[data-cutxi-atelier="product"]').forEach(productUpdate); }
  async function refreshCart() {
    if(!config?.enabled) return;
    if(inflight){pending=true;return;} inflight=true;
    try {
      const path=(root.Shopify?.routes?.root || '/')+'cart?sections=cutxi-atelier-cart-data';
      const response=await fetch(path,{credentials:'same-origin',cache:'no-store'});
      if(!response.ok) throw Error('cart section');
      const data=await response.json();
      const doc=new DOMParser().parseFromString(data['cutxi-atelier-cart-data'] || '','text/html');
      const items=JSON.parse(doc.querySelector('[data-atelier-cart-data]').textContent);
      const result=cartRange(items,config);
      document.querySelectorAll('[data-cutxi-atelier="cart"]').forEach(box=>{renderBox(box,result.range,'cart');box.hidden=!result.count;});
    } catch {
      document.querySelectorAll('[data-cutxi-atelier="cart"]').forEach(box=>renderBox(box,null,'cart'));
    } finally {inflight=false;if(pending){pending=false;scheduleCart();}}
  }
  function scheduleCart() {clearTimeout(timer);timer=setTimeout(refreshCart,300);}
  function init() {
    readConfig();productsUpdate();scheduleCart();
    document.addEventListener('change',productsUpdate);
    document.addEventListener('input',productsUpdate);
    document.addEventListener('submit',productsUpdate,true);
    document.addEventListener('click',event=>{if(event.target.closest('[data-add-to-cart]'))productsUpdate();},true);
    ['ajaxProduct:added','cart:updated','cart:refresh','ajaxCart:updated','cart:build','cart:change','cutxi:production-priority-synced'].forEach(name=>document.addEventListener(name,scheduleCart));
    document.addEventListener('shopify:section:load',()=>{readConfig();productsUpdate();scheduleCart();});
    // Observe only added controls in product forms, never own text or the entire body.
    const observer=new MutationObserver(records=>{if(records.some(r=>Array.from(r.addedNodes).some(n=>n.nodeType===1 && (n.matches('input,select,[data-cutxi-atelier]') || n.querySelector('input,select,[data-cutxi-atelier]')))))productsUpdate();});
    document.querySelectorAll('[data-product-blocks]').forEach(el=>observer.observe(el,{childList:true,subtree:true}));
    document.addEventListener('visibilitychange',()=>{if(!document.hidden){productsUpdate();scheduleCart();}});
    let day=iso(intakeDate(config));
    setInterval(()=>{if(!document.hidden){productsUpdate();const today=iso(intakeDate(config));if(today!==day){day=today;scheduleCart();}}},60000); // Recalculate when the intake date changes, including the 16h cutoff.
  }
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',init);else init();
}(typeof window!=='undefined'?window:globalThis));



